/**
 * The dock every part panel sits in (DECISIONS #94): a grip on the top edge,
 * one remembered height, a header (the kind, the name, whatever chips the
 * caller adds, the one-sentence what, and any lines under it), a ghost close,
 * a scrolling body, and an optional footer. The drawing's part panel and the
 * runtime map's part panel are the same dock with different contents.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import { CloseIcon, IconButton } from '../../lib/ui';
import { ResizeGrip, usePanelResize } from './usePanelResize';

export function SectionLabel({ children }: { children: string }) {
  return (
    <div className="text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary">
      {children}
    </div>
  );
}

export function DockedPanel({
  testId,
  attrs = {},
  ariaLabel,
  focusKey,
  kindWord,
  kindTestId,
  title,
  titleTestId,
  chips = null,
  what,
  lines = null,
  onClose,
  closeTestId,
  children,
  footer = null,
  action = null,
}: {
  testId: string;
  /** data attributes on the dock (a part label or id for tests and styles) */
  attrs?: Record<string, string>;
  ariaLabel: string;
  /** focus lands in the dock whenever this changes: a reader who arrived by
   * keyboard is already where the answer is, and Escape has an obvious home */
  focusKey: string;
  kindWord: string;
  kindTestId?: string;
  title: string;
  titleTestId?: string;
  chips?: ReactNode;
  what: string;
  lines?: ReactNode;
  onClose: () => void;
  closeTestId?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** the one thing to do with this part, in the header where it is seen
   * (2026-09-06; owner: "'Add to question' should not be at the bottom") */
  action?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The height is the reader's (2026-09-03): a grip on the top edge, one
  // remembered number for every dock.
  const { height, grip } = usePanelResize();

  useEffect(() => {
    panelRef.current?.focus();
  }, [focusKey]);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={ariaLabel}
      tabIndex={-1}
      data-testid={testId}
      {...attrs}
      style={{ height }}
      className="flex max-h-[75%] min-h-0 shrink-0 flex-col border-t border-default bg-raised outline-none"
    >
      <ResizeGrip grip={grip} />
      <div className="flex shrink-0 items-start gap-2 px-4 pt-1">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span
              data-testid={kindTestId}
              className="shrink-0 rounded-chip border border-default px-1.5 py-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] tracking-[var(--text-micro-tracking)] uppercase text-tertiary"
            >
              {kindWord}
            </span>
            <h2
              data-testid={titleTestId}
              className="mt-0 mb-0 min-w-0 truncate text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] font-semibold text-primary"
            >
              {title}
            </h2>
            {chips}
          </div>
          <p className="mt-1.5 mb-0 text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-secondary">
            {what}
          </p>
          {lines}
        </div>
        {action}
        <IconButton kind="ghost" size="sm" label="Close" onClick={onClose} testId={closeTestId}>
          <CloseIcon />
        </IconButton>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4">{children}</div>

      {footer}
    </div>
  );
}
